import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import Api from "../../Api/Api.json";
import moment from "moment";
import {
  Container,
  Typography,
  Paper,
  Grid2,
  FormControlLabel,
  TextField,
  Checkbox,
  MenuItem,
  Stack,
  Box,
  Button,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useQueryClient } from "@tanstack/react-query";

// Label Mapping
const label_mapping = new Map();
// Patient Information
label_mapping.set("patient_name", "Name");
label_mapping.set("patient_age", "Age");
label_mapping.set("patient_occupation", "Occupation");
label_mapping.set("patient_address", "Address");
label_mapping.set("patient_cell_no", "Cell No");
label_mapping.set("patient_blood_group", "Blood Group");
// Menstrual History
label_mapping.set("mp", "MP (Menstrual Period)");
label_mapping.set("mc", "MC (Menstrual Cycle)");
label_mapping.set("lmp", "LMP (Last Menstrual Period)");
label_mapping.set("edd", "EDD (Expected Date of Delivery)");
// Husband Information
label_mapping.set("husband_name", "Name");
label_mapping.set("husband_age", "Age");
label_mapping.set("husband_occupation", "Occupation");
label_mapping.set("husband_address", "Address");
label_mapping.set("husband_cell_no", "Cell No");
label_mapping.set("husband_blood_group", "Blood Group");
// Obstetric History
label_mapping.set("married_for", "Married For (Years)");
label_mapping.set("parity", "Parity (Number of Children)");
label_mapping.set("gravida", "Gravida (Number of Pregnancies)");
label_mapping.set("abortion_mr", "Abortion/Miscarriage (Number)");
label_mapping.set("alc", "ALC (Any Living Children)");
// Personal History
label_mapping.set("blood_transfusion_husband", "Blood Transfusion (Husband)");
label_mapping.set("blood_transfusion_wife", "Blood Transfusion (Wife)");
label_mapping.set(
  "alcohol_or_cigarette_use_husband",
  "Alcohol or Cigarette Use (Husband)"
);
label_mapping.set(
  "alcohol_or_cigarette_use_wife",
  "Alcohol or Cigarette Use (Wife)"
);
label_mapping.set("living_abroad_husband", "Living Abroad (Husband)");
label_mapping.set("living_abroad_wife", "Living Abroad (Wife)");
label_mapping.set("history_of_std_husband", "History of STD (Husband)");
label_mapping.set("history_of_std_wife", "History of STD (Wife)");
label_mapping.set("exposure_history_husband", "Exposure History (Husband)");
label_mapping.set("exposure_history_wife", "Exposure History (Wife)");
// Immunization History
label_mapping.set("tetanus", "Tetanus (Doses)");
label_mapping.set("hepatitis_B", "Hepatitis B (Doses)");
label_mapping.set("rubella", "Rubella");
label_mapping.set("others", "Others");
// Investigations
label_mapping.set("CBC", "CBC (Complete Blood Count)");
label_mapping.set(
  "Blood_Glucose_after_2_hours",
  "Blood Glucose (2 Hours After 75g Glucose)"
);
label_mapping.set("TSH", "TSH (Thyroid Stimulating Hormone)");
label_mapping.set("FT4", "FT4 (Free Thyroxine)");
label_mapping.set(
  "urine_rme_cs",
  "Urine RME + CS (Routine Microscopic Examination and Culture Sensitivity)"
);
label_mapping.set("VDRL", "VDRL (Syphilis Test)");
label_mapping.set(
  "HbsAg_anti_hcv",
  "HbsAg / Anti HCV (Hepatitis B and C Test)"
);
label_mapping.set("HIV", "HIV Test");
label_mapping.set("Ultrasonogram", "USG (Ultrasound)");
// Antenatal Examination
label_mapping.set("Date", "Date");
label_mapping.set("Gestation_weeks", "Gestation Weeks");
label_mapping.set("weight", "Weight (kg)");
label_mapping.set("pulse", "Pulse (bpm)");
label_mapping.set("blood_pressure", "Blood Pressure (BP)");
label_mapping.set("anemia", "Anemia");
label_mapping.set("Edema", "Edema");
label_mapping.set("SFH_cm", "SFH (Symphysis Fundal Height in cm)");
label_mapping.set("Lie_presentation", "Lie / Presentation");
label_mapping.set("Liquor_volume", "Liquor Volume");
label_mapping.set("Fetal_movement", "Fetal Movement");
label_mapping.set("Fetal_heart_rate", "Fetal Heart Rate (bpm)");
label_mapping.set("Urine_protein", "Urine Protein");
label_mapping.set("Urine_glucose", "Urine Glucose");
// Postnatal Information
label_mapping.set("delivery_date", "Delivery Date");
label_mapping.set(
  "babyStatus",
  "Baby Status (Alive/Stillbirth/Neonatal Death)"
);
label_mapping.set("Place_of_delivery", "Place of Delivery (Home/Hospital)");
label_mapping.set("baby_weight", "Birth Weight");
label_mapping.set(
  "Mode_of_delivery",
  "Mode of Delivery (Normal/Breech/Forceps/Ventouse/Cesarean)"
);
label_mapping.set("Post_Partum_Complications", "Post Partum Complications");

// Type Mapping
const type_mapping = new Map();
// Patient Information
type_mapping.set("patient_name", "text");
type_mapping.set("patient_age", "number");
type_mapping.set("patient_occupation", "text");
type_mapping.set("patient_address", "text");
type_mapping.set("patient_cell_no", "text");
type_mapping.set("patient_blood_group", "text");
// Menstrual History
type_mapping.set("mp", "text");
type_mapping.set("mc", "text");
type_mapping.set("lmp", "text");
type_mapping.set("edd", "text");
// Husband Information
type_mapping.set("husband_name", "text");
type_mapping.set("husband_age", "number");
type_mapping.set("husband_occupation", "text");
type_mapping.set("husband_address", "text");
type_mapping.set("husband_cell_no", "text");
type_mapping.set("husband_blood_group", "text");
// Obstetric History
type_mapping.set("married_for", "number");
type_mapping.set("parity", "number");
type_mapping.set("gravida", "number");
type_mapping.set("abortion_mr", "number");
type_mapping.set("alc", "number");
// Personal History
type_mapping.set("blood_transfusion_husband", "checkbox");
type_mapping.set("blood_transfusion_wife", "checkbox");
type_mapping.set("alcohol_or_cigarette_use_husband", "checkbox");
type_mapping.set("alcohol_or_cigarette_use_wife", "checkbox");
type_mapping.set("living_abroad_husband", "checkbox");
type_mapping.set("living_abroad_wife", "checkbox");
type_mapping.set("history_of_std_husband", "checkbox");
type_mapping.set("history_of_std_wife", "checkbox");
type_mapping.set("exposure_history_husband", "checkbox");
type_mapping.set("exposure_history_wife", "checkbox");
// Immunization History
type_mapping.set("tetanus", "text");
type_mapping.set("hepatitis_B", "text");
type_mapping.set("rubella", "text");
type_mapping.set("others", "text");
// Investigations
type_mapping.set("CBC", "text");
type_mapping.set("Blood_Glucose_after_2_hours", "text");
type_mapping.set("TSH", "text");
type_mapping.set("FT4", "text");
type_mapping.set("urine_rme_cs", "text");
type_mapping.set("VDRL", "text");
type_mapping.set("HbsAg_anti_hcv", "text");
type_mapping.set("HIV", "text");
type_mapping.set("Ultrasonogram", "text");
// Antenatal Examination
type_mapping.set("Date", "date");
type_mapping.set("Gestation_weeks", "number");
type_mapping.set("weight", "text");
type_mapping.set("pulse", "text");
type_mapping.set("blood_pressure", "text");
type_mapping.set("anemia", "text");
type_mapping.set("Edema", "text");
type_mapping.set("SFH_cm", "text");
type_mapping.set("Lie_presentation", "text");
type_mapping.set("Liquor_volume", "text");
type_mapping.set("Fetal_movement", "text");
type_mapping.set("Fetal_heart_rate", "text");
type_mapping.set("Urine_protein", "text");
type_mapping.set("Urine_glucose", "text");
// Postnatal Information
type_mapping.set("delivery_date", "date");
type_mapping.set("babyStatus", ["Alive", "Stillbirth", "Neonatal Death"]);
type_mapping.set("Place_of_delivery", ["Home", "Hospital"]);
type_mapping.set("baby_weight", "text");
type_mapping.set("Mode_of_delivery", [
  "Normal",
  "Breech",
  "Forceps",
  "Ventouse",
  "Cesarean",
]);
type_mapping.set("Post_Partum_Complications", "text");

const sectionsByStep = [
  ["patient_info", "menstrual_history", "husband_info"],
  ["obstetric_history", "personal_history", "immunization_history"],
  ["investigations"],
  ["antenatal_examination"],
  ["postnatal_info"],
];

const ANCIntake = ({ patient_id, setSelectedDate, setIsIntake }) => {
  const queryClient = useQueryClient();
  const initialFormData = {
    patient_info: {
      label: "Patient Information",
      patient_name: "",
      patient_age: "",
      patient_occupation: "",
      patient_address: "",
      patient_cell_no: "",
      patient_blood_group: "",
      registration_number: "",
    },
    menstrual_history: {
      label: "Menstrual History",
      mp: "",
      mc: "",
      lmp: "",
      edd: "",
    },
    husband_info: {
      label: "Husband Information",
      husband_name: "",
      husband_age: "",
      husband_occupation: "",
      husband_address: "",
      husband_cell_no: "",
      husband_blood_group: "",
    },
    obstetric_history: {
      label: "Obstetric History",
      married_for: "",
      parity: "",
      gravida: "",
      abortion_mr: "",
      alc: "",
    },
    personal_history: {
      label: "Personal History",
      blood_transfusion_husband: false,
      blood_transfusion_wife: false,
      alcohol_or_cigarette_use_husband: false,
      alcohol_or_cigarette_use_wife: false,
      living_abroad_husband: false,
      living_abroad_wife: false,
      history_of_std_husband: false,
      history_of_std_wife: false,
      exposure_history_husband: false,
      exposure_history_wife: false,
    },
    immunization_history: {
      label: "Immunization History",
      tetanus: "",
      hepatitis_B: "",
      rubella: "",
      others: "",
    },
    investigations: {
      label: "Investigations",
      CBC: "",
      Blood_Glucose_after_2_hours: "",
      TSH: "",
      FT4: "",
      urine_rme_cs: "",
      VDRL: "",
      HbsAg_anti_hcv: "",
      HIV: "",
      Ultrasonogram: "",
    },
    antenatal_examination: {
      label: "Antenatal Examination",
      Date: "",
      Gestation_weeks: "",
      weight: "",
      pulse: "",
      blood_pressure: "",
      anemia: "",
      Edema: "",
      SFH_cm: "",
      Lie_presentation: "",
      Liquor_volume: "",
      Fetal_movement: "",
      Fetal_position: "",
      Fetal_heart_rate: "",
      Urine_protein: "",
      Urine_glucose: "",
    },
    postnatal_info: {
      label: "Postnatal Information",
      delivery_date: "",
      babyStatus: "",
      Place_of_delivery: "",
      baby_weight: "",
      Mode_of_delivery: "",
      Post_Partum_Complications: "",
    },
  };

  const topRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeStep]);

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({ defaultValues: initialFormData });

  const onSubmit = (values) => {
    // console.log("Form submitted:", values);
    const requestData = {
      task: "submitANCDataForm",
      query_dict: values,
      patient_id: patient_id,
      creation_date: moment().format("YYYY-MM-DD HH:mm:ss"),
    };
    const headers = { headers: { "Content-Type": "application/json" } };
    const url = Api.PREGNANCY_DATA_API;

    axios
      .post(url, requestData, headers)
      .then((response) => {
        if (response.data) {
          toast.success("Form submitted successfully");
          setTimeout(() => {
            queryClient.invalidateQueries(["ancDates", patient_id]);
            setSelectedDate(null);
            setIsIntake(false);
          }, 300);
        }
      })
      .catch((error) => {
        toast.error("Error submitting form. Please try again.");
      });
  };

  const handleNext = async (event) => {
    event.preventDefault();
    const currentSections = sectionsByStep[activeStep];
    const fieldKeys = currentSections.flatMap((section) => {
      return Object.keys(initialFormData[section])
        .filter(
          (key) =>
            ![
              "label",
              "registration_number",
              "husband_address",
              "Fetal_position",
            ].includes(key)
        )
        .map((key) => `${section}.${key}`);
    });
    const valid = await trigger(fieldKeys);
    if (valid) {
      setActiveStep((s) => s + 1);
    }
  };

  const handleBack = (event) => {
    event.preventDefault();
    setActiveStep((s) => s - 1);
  };

  const fieldStyles = {
    backgroundColor: "#ffffff", // pure white
    "& .MuiInputLabel-root": { color: "#888888", textAlign: "center" }, // lighter label color
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#CCCCCC" },
      "&:hover fieldset": { borderColor: "#999999" },
      "&.Mui-focused fieldset": { borderColor: "#1976d2" },
    },
    color: "#333333",
  };

  const renderField = (sectionKey, fieldKey) => {
    const name = `${sectionKey}.${fieldKey}`;
    const type = type_mapping.get(fieldKey);
    const label = label_mapping.get(fieldKey);
    const error = !!errors?.[sectionKey]?.[fieldKey];
    const helperText = errors?.[sectionKey]?.[fieldKey]?.message;
    const isTextarea = [
      "patient_address",
      "others",
      "Post_Partum_Complications",
    ].includes(fieldKey);

    // 1️⃣ Checkbox
    if (type === "checkbox") {
      return (
        <FormControlLabel
          key={name}
          control={
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  sx={{
                    color: "#1976d2",
                    "&.Mui-checked": { color: "#115293" },
                  }}
                />
              )}
            />
          }
          label={label}
          sx={{
            mb: 2,
            color: "#666666",
            "& .MuiFormControlLabel-label": {
              fontWeight: 500,
              textAlign: "center",
              width: "100%",
            },
          }}
        />
      );
    }

    // 2️⃣ Select
    if (Array.isArray(type)) {
      return (
        <Stack key={name} spacing={1} sx={{ mb: 2 }}>
          <Controller
            name={name}
            control={control}
            rules={{ required: "Please select an option" }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label={label}
                error={error}
                helperText={helperText}
                variant="outlined"
                sx={fieldStyles}
              >
                {type.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      );
    }

    // 3️⃣ Input/Text/Textarea
    return (
      <Stack key={name} spacing={1} sx={{ mb: 2 }}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const { value, onChange, ...restField } = field;
            const formattedValue =
              type === "date"
                ? value
                  ? moment(value).format("YYYY-MM-DD")
                  : ""
                : value;
            return (
              <TextField
                {...restField}
                value={formattedValue}
                fullWidth
                type={type}
                onChange={(e) => {
                  const { valueAsNumber, valueAsDate, value } = e.target;
                  if (type === "number") {
                    field.onChange(isNaN(valueAsNumber) ? null : valueAsNumber);
                  } else if (type === "date") {
                    field.onChange(valueAsDate ?? null);
                  } else {
                    field.onChange(value);
                  }
                }}
                placeholder={label}
                variant="outlined"
                multiline={isTextarea}
                rows={isTextarea ? 4 : undefined}
                slotProps={{
                  inputLabel: {
                    shrink: false, // prevent floating label
                  },
                  input: {
                    sx: {
                      textAlign: "center", // center text horizontally
                    },
                  },
                }}
                error={error}
                helperText={helperText}
                sx={fieldStyles}
              />
            );
          }}
        />
      </Stack>
    );
  };

  return (
    <>
      <div ref={topRef}></div>
      <Container
        maxWidth="md"
        sx={{
          py: 4,
          px: { xs: 1, sm: 2 },
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ color: "#1976d2", fontWeight: 600 }}
        >
          Antenatal Card Form
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{ mb: 4, px: { xs: 1, sm: 0 } }}
        >
          {Array.from({ length: sectionsByStep.length }).map((_, idx) => (
            <Step key={idx}>
              <StepLabel>Step {idx + 1}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid2
            container
            spacing={4}
            justifyContent="center"
            direction="column"
          >
            {sectionsByStep[activeStep].map((sectionKey) => (
              <Grid2 item key={sectionKey}>
                <Paper
                  elevation={3}
                  sx={{
                    mx: "auto",
                    p: { xs: 2, sm: 3 }, // Reduce padding on mobile
                    width: "100%", // Always full width within container
                    maxWidth: { xs: "100%", sm: 700 }, // Max width only on larger screens
                    backgroundColor: "#fafafa", // off-white card background
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ color: "#1976d2", fontWeight: 500 }}
                  >
                    {initialFormData[sectionKey].label}
                  </Typography>
                  {Object.keys(initialFormData[sectionKey])
                    .filter(
                      (key) =>
                        ![
                          "label",
                          "registration_number",
                          "husband_address",
                          "Fetal_position",
                        ].includes(key)
                    )
                    .map((fieldKey) => renderField(sectionKey, fieldKey))}
                </Paper>
              </Grid2>
            ))}
            <Grid2 item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                mt={4}
                width="100%" // Always full width
                maxWidth={{ xs: "100%", sm: 700 }} // Max width only on larger screens
                mx="auto"
              >
                <Button
                  type="button"
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="contained"
                  sx={{
                    backgroundColor: "#e0e0e0",
                    color: "#333333",
                    "&:hover": { backgroundColor: "#d5d5d5" },
                  }}
                >
                  Previous
                </Button>

                {activeStep < sectionsByStep.length - 1 ? (
                  <Button
                    type="button"
                    onClick={handleNext}
                    variant="contained"
                  >
                    Next
                  </Button>
                ) : (
                  <Button type="submit" variant="contained">
                    Submit
                  </Button>
                )}
              </Box>
            </Grid2>
          </Grid2>
        </form>
      </Container>
    </>
  );
};

export default ANCIntake;
